<script setup lang="ts">
import { computed } from 'vue';
import type {
  ChangelogEntry,
  ChangelogAction,
  CatalogueItemGroup,
} from '@/models/Catalogue';
import { getAllCatalogueGroupsFlat } from '@/composables/catalogue/useCatalogueGroupHierarchy';
import gettext from '@/i18n/gettext';
import formatDate from '@/helpers/DateFormatterHelper';

const { $gettext } = gettext;

const props = defineProps<{
  changelog: ChangelogEntry[];
  groups: CatalogueItemGroup[];
}>();

const flatGroups = computed(() => getAllCatalogueGroupsFlat(props.groups));

interface ActionMeta {
  icon: string;
  color: string;
  label: string;
}

const actionMeta = computed<Record<ChangelogAction, ActionMeta>>(() => ({
  created: {
    icon: 'mdi-plus-circle',
    color: 'success',
    label: $gettext('Created item'),
  },
  updated: {
    icon: 'mdi-pencil',
    color: 'info',
    label: $gettext('Updated item'),
  },
  duplicated: {
    icon: 'mdi-content-copy',
    color: 'primary',
    label: $gettext('Duplicated item'),
  },
  revised: {
    icon: 'mdi-source-branch-plus',
    color: 'primary',
    label: $gettext('Created revision'),
  },
  childAdded: {
    icon: 'mdi-link-plus',
    color: 'success',
    label: $gettext('Added component'),
  },
  childRemoved: {
    icon: 'mdi-link-off',
    color: 'error',
    label: $gettext('Removed component'),
  },
  childQuantityUpdated: {
    icon: 'mdi-counter',
    color: 'info',
    label: $gettext('Updated component quantity'),
  },
  imageAdded: {
    icon: 'mdi-image-plus',
    color: 'success',
    label: $gettext('Added image'),
  },
  imageRemoved: {
    icon: 'mdi-image-remove',
    color: 'error',
    label: $gettext('Removed image'),
  },
  fileAdded: {
    icon: 'mdi-file-plus',
    color: 'success',
    label: $gettext('Added file'),
  },
  fileRemoved: {
    icon: 'mdi-file-remove',
    color: 'error',
    label: $gettext('Removed file'),
  },
  erpImported: {
    icon: 'mdi-database-import',
    color: 'warning',
    label: $gettext('ERP import'),
  },
  promoted: {
    icon: 'mdi-arrow-up-circle',
    color: 'success',
    label: $gettext('Promoted to standard'),
  },
}));

const getActionMeta = (action: ChangelogAction): ActionMeta => {
  return (
    actionMeta.value[action] ?? {
      icon: 'mdi-circle-small',
      color: 'grey',
      label: action,
    }
  );
};

const fieldLabels = computed<Record<string, string>>(() => ({
  name: $gettext('Name'),
  description: $gettext('Description'),
  cost: $gettext('Cost'),
  currency: $gettext('Currency'),
  category: $gettext('Category'),
  groupUuid: $gettext('Group'),
  erpId: $gettext('Xentral ID'),
  hyperlink: $gettext('Hyperlink'),
  drawingNumber: $gettext('Drawing Number'),
  isAssembly: $gettext('Type'),
  weightG: $gettext('Weight'),
  strokeMm: $gettext('Stroke'),
  maxGripForceN: $gettext('Max Grip Force'),
  ipClass: $gettext('IP Class'),
  payloadKg: $gettext('Payload'),
  reachMm: $gettext('Reach'),
  dof: $gettext('Degrees of Freedom'),
  activationPrinciple: $gettext('Activation Principle'),
  materialType: $gettext('Material Type'),
  gripControlSensor: $gettext('Grip Control Sensor'),
  distanceSensor: $gettext('Distance Sensor'),
  airSupplyNeeded: $gettext('Air Supply Needed'),
  vacuumSupplyNeeded: $gettext('Vacuum Supply Needed'),
  pneumaticTubeDiameterMm: $gettext('Pneumatic Tube Diameter'),
  robotVersion: $gettext('Robot Version'),
}));

const formatFieldLabel = (field: string): string => {
  return fieldLabels.value[field] ?? field;
};

const resolveGroupName = (uuid: unknown): string => {
  if (typeof uuid !== 'string') {
    return '—';
  }
  const group = flatGroups.value.find((g) => g.uuid === uuid);
  return group?.name ?? String(uuid);
};

const formatValue = (value: unknown, field?: string): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (field === 'groupUuid') {
    return resolveGroupName(value);
  }
  if (field === 'isAssembly') {
    return value ? $gettext('Assembly') : $gettext('Part');
  }
  if (typeof value === 'boolean') {
    return value ? $gettext('Yes') : $gettext('No');
  }
  return String(value);
};

/**
 * Checks if an action type uses field-level old/new diffs.
 */
const isFieldDiffAction = (action: ChangelogAction): boolean => {
  return action === 'updated' || action === 'erpImported';
};
</script>

<template>
  <div class="pa-4">
    <v-timeline
      v-if="changelog.length > 0"
      density="compact"
      side="end"
      align="start"
    >
      <v-timeline-item
        v-for="entry in changelog"
        :key="entry.uuid"
        :dot-color="getActionMeta(entry.action).color"
        :icon="getActionMeta(entry.action).icon"
        size="small"
      >
        <div class="d-flex flex-column">
          <div class="d-flex align-center flex-wrap ga-2">
            <span class="font-weight-medium">{{ entry.userName }}</span>
            <span class="text-medium-emphasis">
              {{ getActionMeta(entry.action).label }}
            </span>
            <span class="text-caption text-medium-emphasis">
              {{ formatDate(entry.timestamp, true) }}
            </span>
          </div>

          <!-- Field-level diffs for updated / erpImported -->
          <div
            v-if="
              isFieldDiffAction(entry.action) &&
              entry.changes &&
              Object.keys(entry.changes).length > 0
            "
            class="mt-2"
          >
            <div
              v-for="(change, field) in entry.changes"
              :key="field"
              class="d-flex align-center ga-1 text-body-2 mb-1"
            >
              <span class="text-medium-emphasis">
                {{ formatFieldLabel(field as string) }}:
              </span>
              <span class="text-grey">
                {{
                  formatValue(change.old ?? change.previous, field as string)
                }}
              </span>
              <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
              <span class="font-weight-medium">
                {{ formatValue(change.new, field as string) }}
              </span>
            </div>
          </div>

          <!-- Duplicated: source item name -->
          <div
            v-else-if="entry.action === 'duplicated' && entry.changes"
            class="mt-1 text-body-2 text-medium-emphasis"
          >
            {{ $gettext('Source') }}: {{ entry.changes.sourceItemName }}
          </div>

          <!-- Revised: version change -->
          <div
            v-else-if="entry.action === 'revised' && entry.changes"
            class="mt-1 text-body-2 d-flex align-center ga-1"
          >
            <span class="text-medium-emphasis">{{ $gettext('Version') }}:</span>
            <span class="text-grey">
              {{ formatValue(entry.changes.previousVersion) }}
            </span>
            <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
            <span class="font-weight-medium">
              {{ formatValue(entry.changes.newVersion) }}
            </span>
          </div>

          <!-- Child added -->
          <div
            v-else-if="entry.action === 'childAdded' && entry.changes"
            class="mt-1 text-body-2 text-medium-emphasis"
          >
            {{ entry.changes.childName }}
            <span v-if="entry.changes.quantity">
              ({{ $gettext('Qty') }}: {{ entry.changes.quantity }})
            </span>
          </div>

          <!-- Child removed -->
          <div
            v-else-if="entry.action === 'childRemoved' && entry.changes"
            class="mt-1 text-body-2 text-medium-emphasis"
          >
            {{ entry.changes.childName }}
          </div>

          <!-- Child quantity updated -->
          <div
            v-else-if="entry.action === 'childQuantityUpdated' && entry.changes"
            class="mt-1 text-body-2 d-flex align-center ga-1"
          >
            <span class="text-medium-emphasis">
              {{ entry.changes.childName }}:
            </span>
            <span class="text-grey">
              {{ formatValue(entry.changes.quantity?.old) }}
            </span>
            <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
            <span class="font-weight-medium">
              {{ formatValue(entry.changes.quantity?.new) }}
            </span>
          </div>

          <!-- Image / file added or removed -->
          <div
            v-else-if="
              [
                'imageAdded',
                'imageRemoved',
                'fileAdded',
                'fileRemoved',
              ].includes(entry.action) && entry.changes
            "
            class="mt-1 text-body-2 text-medium-emphasis"
          >
            {{ entry.changes.filename }}
          </div>

          <!-- Promoted: category change -->
          <div
            v-else-if="entry.action === 'promoted' && entry.changes"
            class="mt-1 text-body-2 d-flex align-center ga-1"
          >
            <span class="text-medium-emphasis">
              {{ $gettext('Category') }}:
            </span>
            <span class="text-grey">
              {{ formatValue(entry.changes.category?.old) }}
            </span>
            <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
            <span class="font-weight-medium">
              {{ formatValue(entry.changes.category?.new) }}
            </span>
          </div>
        </div>
      </v-timeline-item>
    </v-timeline>

    <v-alert
      v-else
      type="info"
      variant="tonal"
      :text="$gettext('No activity recorded yet.')"
    />
  </div>
</template>

<style scoped>
:deep(.v-timeline-item) {
  padding-bottom: 16px;
}
</style>
